/**
 * Shared accessibility primitives for lead-capture forms.
 *
 * - `AccessibleField` wires label ↔ control ↔ error message with real ids,
 *   `aria-describedby`, `aria-invalid`, and `aria-required`.
 * - `FormErrorSummary` renders a focusable, keyboard-navigable list of errors
 *   announced via `role="alert"`, with links that move focus to the field.
 * - `focusFirstError` moves focus on validation failure.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { AlertCircle } from "lucide-react";

export type ControlProps = {
  id: string;
  name: string;
  "aria-invalid": boolean | undefined;
  "aria-describedby": string | undefined;
  "aria-required": boolean | undefined;
};

export function fieldIds(formId: string, name: string) {
  return {
    controlId: `${formId}-${name}`,
    errorId: `${formId}-${name}-error`,
    hintId: `${formId}-${name}-hint`,
  };
}

export function AccessibleField({
  formId,
  name,
  label,
  error,
  hint,
  required,
  className,
  children,
}: {
  formId: string;
  name: string;
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  className?: string;
  children: (props: ControlProps) => ReactNode;
}) {
  const { controlId, errorId, hintId } = fieldIds(formId, name);
  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={className ?? "text-left"}>
      <label
        htmlFor={controlId}
        className="text-[10px] uppercase tracking-[0.2em] text-silver"
      >
        {label}
        {required && (
          <span aria-hidden="true" className="ml-1 text-destructive">
            *
          </span>
        )}
      </label>
      <div className="mt-2">
        {children({
          id: controlId,
          name,
          "aria-invalid": error ? true : undefined,
          "aria-describedby": describedBy || undefined,
          "aria-required": required || undefined,
        })}
      </div>
      {hint && (
        <span id={hintId} className="mt-1.5 block text-xs text-muted-foreground">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="mt-1.5 block text-xs text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}

/** Moves keyboard focus to the first field that failed validation. */
export function focusFirstError(formId: string, order: string[], errors: Record<string, string | undefined>) {
  const first = order.find((key) => errors[key]);
  if (!first) return;
  if (typeof document === "undefined") return;
  const el = document.getElementById(fieldIds(formId, first).controlId) as HTMLElement | null;
  el?.focus();
  el?.scrollIntoView?.({ block: "center", behavior: "smooth" });
}

export function FormErrorSummary({
  formId,
  title = "Please fix the following before continuing:",
  order,
  errors,
  labels,
}: {
  formId: string;
  title?: string;
  order: string[];
  errors: Record<string, string | undefined>;
  labels: Record<string, string>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const listed = order.filter((key) => errors[key]);

  useEffect(() => {
    if (listed.length) ref.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listed.join("|")]);

  if (!listed.length) return null;

  return (
    <div
      ref={ref}
      role="alert"
      tabIndex={-1}
      aria-labelledby={`${formId}-error-summary-title`}
      className="mt-5 border border-destructive/40 bg-destructive/10 p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
    >
      <p
        id={`${formId}-error-summary-title`}
        className="flex items-center gap-2 text-sm font-semibold text-foreground"
      >
        <AlertCircle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        {title}
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-8 text-sm text-foreground">
        {listed.map((key) => (
          <li key={key}>
            <a
              href={`#${fieldIds(formId, key).controlId}`}
              className="underline underline-offset-2 hover:no-underline focus-visible:ring-2 focus-visible:ring-destructive"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(fieldIds(formId, key).controlId);
                el?.focus();
                el?.scrollIntoView?.({ block: "center", behavior: "smooth" });
              }}
            >
              {labels[key] ?? key}: {errors[key]}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
